import { GetServerSidePropsContext } from 'next'
import { parseCookies, setCookie } from 'nookies'

const ActiveExperimentsKEY = 'CONFIG'

export type ActiveExperiments = Record<ExperimentKey, ExperimentVariant>

export enum ExperimentKey {
    test = 'TEST',
    showMobileKnowPageRevamp = 'SHOW_MOBILE_KNOW_PAGE_REVAMP',
    showKnowledgeAreaBreadcrumbs = 'SHOW_KNOWLEDGE_AREA_BREADCRUMBS',
    showUpdatedOnwithinKnowHeader = 'SHOW_UPDATED_ON_WITHIN_KNOW_HEADER',
    unblurEveryOtherGEOKnowPage = 'UNBLUR_EVERY_OTHER_GEO_KNOW_PAGE',
    showPersonalizedAcquisitionFunnel = 'SHOW_PERSONALIZED_ACQUISITION_FUNNEL',
}

export enum ExperimentVariant {
    // Testing and Development
    testBaseline = 'TEST_0',
    testVariant = 'TEST_1',

    // Mobile User Experience
    showMobileKnowPageRevampBaseline = 'SHOW_MOBILE_KNOW_PAGE_REVAMP_ITERATION_2_VARIANT_0',
    showMobileKnowPageRevampVariant = 'SHOW_MOBILE_KNOW_PAGE_REVAMP_ITERATION_2_VARIANT_1',

    // Navigation and UI Components
    showKnowledgeAreaBreadcrumbsBaseline = 'SHOW_KNOWLEDGE_AREA_BREADCRUMBS_VARIANT_0',
    showKnowledgeAreaBreadcrumbsVariant = 'SHOW_KNOWLEDGE_AREA_BREADCRUMBS_VARIANT_1',

    // Updated on date within know header
    showUpdatedOnwithinKnowHeaderBaseline = 'SHOW_UPDATED_ON_WITHIN_KNOW_HEADER_VARIANT_0',
    showUpdatedOnwithinKnowHeaderVariant = 'SHOW_UPDATED_ON_WITHIN_KNOW_HEADER_VARIANT_1',

    // Content Access Control
    unblurEveryOtherGEOKnowPageBaseline = 'UNBLUR_EVERY_OTHER_GEO_KNOW_PAGE_VARIANT_0',
    unblurEveryOtherGEOKnowPageVariant = 'UNBLUR_EVERY_OTHER_GEO_KNOW_PAGE_VARIANT_1',

    // Personalized Acquisition Funnel
    showPersonalizedAcquisitionFunnelBaseline = 'SHOW_PERSONALIZED_ACQUISITION_FUNNEL_VARIANT_0',
    showPersonalizedAcquisitionFunnelVariant = 'SHOW_PERSONALIZED_ACQUISITION_FUNNEL_VARIANT_1',
    showPersonalizedAcquisitionFunnelVariant2 = 'SHOW_PERSONALIZED_ACQUISITION_FUNNEL_VARIANT_2',
}

function getRandomVariant(variants: ExperimentVariant[]): ExperimentVariant {
    return variants[Math.floor(Math.random() * variants.length)]
}

function getStoredVariant(storedVariants: string[], variants: ExperimentVariant[]): ExperimentVariant | null {
    for (const variant of variants) {
        if (storedVariants.includes(variant)) {
            return variant
        }
    }

    return null
}

function getExperimentVariant(possibleVariants: ExperimentVariant[], storedVariants: string[]): ExperimentVariant {
    const storedVariant = getStoredVariant(storedVariants, possibleVariants)
    if (storedVariant !== null) {
        return storedVariant
    } else {
        return getRandomVariant(possibleVariants)
    }
}

export function getActiveExperiments(ctx?: GetServerSidePropsContext): ActiveExperiments {
    const storedVariants = getActiveExperimentsStore(ctx)

    const activeExperiments: ActiveExperiments = {
        TEST: getExperimentVariant([ExperimentVariant.testBaseline, ExperimentVariant.testVariant], storedVariants),
        SHOW_MOBILE_KNOW_PAGE_REVAMP: getExperimentVariant(
            [ExperimentVariant.showMobileKnowPageRevampBaseline, ExperimentVariant.showMobileKnowPageRevampVariant],
            storedVariants
        ),
        SHOW_KNOWLEDGE_AREA_BREADCRUMBS: getExperimentVariant(
            [ExperimentVariant.showKnowledgeAreaBreadcrumbsBaseline, ExperimentVariant.showKnowledgeAreaBreadcrumbsVariant],
            storedVariants
        ),
        SHOW_UPDATED_ON_WITHIN_KNOW_HEADER: getExperimentVariant(
            [
                ExperimentVariant.showUpdatedOnwithinKnowHeaderBaseline,
                ExperimentVariant.showUpdatedOnwithinKnowHeaderVariant,
            ],
            storedVariants
        ),
        UNBLUR_EVERY_OTHER_GEO_KNOW_PAGE: getExperimentVariant(
            [ExperimentVariant.unblurEveryOtherGEOKnowPageBaseline, ExperimentVariant.unblurEveryOtherGEOKnowPageVariant],
            storedVariants
        ),
        SHOW_PERSONALIZED_ACQUISITION_FUNNEL: getExperimentVariant(
            [
                ExperimentVariant.showPersonalizedAcquisitionFunnelBaseline,
                ExperimentVariant.showPersonalizedAcquisitionFunnelVariant,
                ExperimentVariant.showPersonalizedAcquisitionFunnelVariant2,
            ],
            storedVariants
        ),
    }

    storeActiveExperiments(Object.values(activeExperiments), ctx)

    return activeExperiments
}

export function getActiveExperimentsVariants() {
    const activeExperiments = getActiveExperiments()
    return Object.values(activeExperiments)
}

// Active Experiments
export function getActiveExperimentsStore(ctx?: GetServerSidePropsContext): string[] {
    const cookies = parseCookies(ctx)
    const item = cookies[ActiveExperimentsKEY]
    if (!item) {
        return []
    }
    return item.split(',')
}

export const storeActiveExperiments = (experiments: string[], ctx?: GetServerSidePropsContext) => {
    const stringifiedValue = experiments.join(',')
    setCookie(ctx, ActiveExperimentsKEY, stringifiedValue, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
    })
}
